import type { DocumentDto } from '@app/contracts';
import { useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useCallback, useState } from 'react';
import { documentViewSearch } from '../../../router';
import { useAuthStore } from '../../auth/store.js';
import { DocumentSpecificRulesModal } from './DocumentSpecificRulesModal.js';

type Props = {
  document: DocumentDto;
  /** Classes Tailwind pour le bouton (ex. variante compacte dans une barre d'outils) */
  className?: string;
  /** Ouvre la modale au montage (ex. lien depuis le parcours). */
  autoOpenDocumentRights?: boolean;
};

export function DocumentRightsButton({ document, className, autoOpenDocumentRights }: Props) {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin' || role === 'super_admin';
  const [open, setOpen] = useState(() => Boolean(autoOpenDocumentRights));

  const handleClose = useCallback(() => {
    setOpen(false);
    if (autoOpenDocumentRights) {
      void navigate({
        to: '/documents/$id',
        params: { id: document.id },
        search: documentViewSearch.closed,
        replace: true,
      });
    }
  }, [autoOpenDocumentRights, document.id, navigate]);

  if (!isAdmin) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Droits spécifiques sur ce document"
        className={
          className ??
          'flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50'
        }
      >
        <Shield className="h-4 w-4 text-emerald-600" />
        Droits
      </button>
      {open && <DocumentSpecificRulesModal document={document} onClose={handleClose} />}
    </>
  );
}
